import { Link } from 'react-router-dom';

import type { CoffeeViewNavState } from '@/analytics/events';
import { Avatar } from '@/components/common/Avatar';
import { BrewIcon } from '@/components/common/BrewIcon';
import { RatingDisplay } from '@/components/common/RatingDisplay';
import { useImageFallback } from '@/hooks/useImageFallback';
import { brewTypeLabel } from '@/lib/brewTypes';
import { formatRelativeTime, formatScore } from '@/lib/formatters';
import type { FeedItem } from '@/types/feed';

interface FeedCardProps {
  item: FeedItem;
  className?: string;
}

export function FeedCard({ item, className = '' }: FeedCardProps) {
  const user = item.user;
  const coffee = item.coffee;

  const userName = user ? user.name : 'Someone';
  const userFirstName = user ? user.name : 'Unknown';
  const userLastName = user ? user.surname : 'User';
  const userAvatar = user?.profilePictureLocation ?? null;

  const coffeeName = coffee ? coffee.name : 'Unknown coffee';
  const roasterName = coffee?.roaster?.name ?? 'Unknown roaster';
  const coffeeOrigin = coffee?.origin ?? 'Unknown origin';
  const coffeeImageUrl = coffee?.imageUrl ?? null;
  const { hasError, onError } = useImageFallback(coffeeImageUrl);

  // Exclude the empty string too, not just null: an empty imageUrl must fall
  // through to the text-only layout rather than render `<img src="">`.
  const showImage = coffeeImageUrl !== null && coffeeImageUrl !== '' && !hasError;

  const byline = (
    <>
      <p className="text-black text-sm font-semibold">by {roasterName}</p>
      <p className="text-black text-sm font-semibold opacity-75">{coffeeOrigin}</p>
    </>
  );

  const brewRow = item.brewType ? (
    <>
      <BrewIcon type={item.brewType} className="h-10 w-10 text-wolisho-brown" />
      <span className="text-xs font-semibold text-wolisho-brown">
        Brewed as {brewTypeLabel(item.brewType).toLowerCase()}
      </span>
    </>
  ) : null;

  const card = (
    <article
      className={`bg-wolisho-card rounded-xl p-4 md:p-6 ${className}`}
      data-testid="feed-card"
    >
      {/* Who + when */}
      <div className="flex items-center gap-3 mb-4">
        <Avatar
          src={userAvatar}
          firstName={userFirstName}
          lastName={userLastName}
          size="sm"
        />
        <div>
          <p className="text-black text-lg font-semibold">
            <span className="text-wolisho-red font-bold">{userName}</span>
            {' '}rated a coffee
          </p>
          <p className="text-black/50 text-sm">
            {formatRelativeTime(item.createdAt)}
          </p>
        </div>
      </div>

      {/* Identity block. With an image the coffee sits in a framed cream tile
          (matching the coffee detail page) beside a divided column. On mobile
          the title spans full width above the tile so long names don't wrap
          into the narrow column; on desktop it moves into the column beside
          the tile, right of the divider. */}
      {showImage ? (
        <>
          <h3 className="md:hidden text-black text-2xl font-bold text-balance">
            {coffeeName}
          </h3>
          <div className="mt-4 flex items-stretch gap-4 md:gap-6">
            <div className="w-28 h-28 md:w-32 md:h-32 shrink-0 self-center overflow-hidden rounded-2xl bg-wolisho-cream border border-wolisho-brown/10 shadow-[0_10px_24px_-14px_rgba(54,17,0,0.45)]">
              <img
                src={coffeeImageUrl}
                alt={coffeeName}
                className="w-full h-full object-contain p-2"
                onError={onError}
              />
            </div>
            <div className="flex-1 min-w-0 flex flex-col justify-center border-l border-wolisho-brown/10 pl-4 md:pl-6">
              <h3 className="hidden md:block text-black text-2xl font-bold text-balance mb-1">
                {coffeeName}
              </h3>
              {byline}
              {/* From 400px up the brew line joins the column beside the tile,
                  right of the divider; narrower phones keep it centered below. */}
              {brewRow && (
                <div
                  className="hidden min-[400px]:flex items-center gap-1.5 mt-2"
                  data-testid="feed-brew-type"
                >
                  {brewRow}
                </div>
              )}
            </div>
          </div>
        </>
      ) : (
        <>
          <h3 className="text-black text-2xl md:text-3xl font-bold text-balance">
            {coffeeName}
          </h3>
          <div className="mt-1">{byline}</div>
        </>
      )}

      {/* Tasting note, brew method and score share a centered spine below the
          identity block, so the card reads the same with or without an image. */}
      {item.tastingNotes && (
        <p
          className={`text-sm italic text-black/60 text-balance mt-4 ${
            showImage ? 'text-center' : ''
          }`}
        >
          {`“${item.tastingNotes}”`}
        </p>
      )}

      {/* Centered brew line: always for the image-less card; only on sub-400px
          phones when there is an image (the column carries it from 400px up). */}
      {brewRow && (
        <div
          className={`flex items-center justify-center gap-1.5 mt-4 ${
            showImage ? 'min-[400px]:hidden' : ''
          }`}
          data-testid="feed-brew-type"
        >
          {brewRow}
        </div>
      )}

      <div className="flex items-center justify-center gap-4 mt-4">
        <span
          className="text-5xl md:text-4xl leading-none font-semibold text-wolisho-brown"
          data-testid="score-display"
        >
          {formatScore(item.score)}
        </span>
        <RatingDisplay score={item.score} size="responsive" />
        <span className="sr-only">{item.score} out of 5</span>
      </div>
    </article>
  );

  if (coffee) {
    return (
      <Link
        to={`/coffee/${coffee.id}`}
        state={{ source: 'feed' } satisfies CoffeeViewNavState}
        className="block rounded-xl focus:outline-hidden focus-visible:ring-2 focus-visible:ring-wolisho-red"
        aria-label={`View ${coffeeName}`}
      >
        {card}
      </Link>
    );
  }

  return card;
}
